import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Truck, Users, ShieldCheck, IndianRupee, LifeBuoy, Warehouse, X } from "lucide-react";
import { appApi } from "@/lib/api";

const POLL_MS = 30000;
const SNOOZE_MS = 5 * 60 * 1000;

const CARD_META = {
  merchants: { icon: Users, label: "New merchants onboarded", href: "/admin/merchants", cta: "Review merchants" },
  kyc: { icon: ShieldCheck, label: "KYC submissions waiting", href: "/admin/kyc", cta: "Open KYC queue" },
  cod: { icon: IndianRupee, label: "COD remittance pending", href: "/admin/cod", cta: "Settle COD" },
  tickets: { icon: LifeBuoy, label: "Open support tickets", href: "/admin/tickets", cta: "Reply now" },
  trucks: { icon: Truck, label: "Truck bookings awaiting allocation", href: "/admin/trucks", cta: "Allocate trucks" },
  warehouses: { icon: Warehouse, label: "Warehouses awaiting activation", href: "/admin/warehouses", cta: "Activate warehouses" },
};

export const AdminNotifier = () => {
  const [items, setItems] = useState({});
  const [snoozed, setSnoozed] = useState({});
  const [open, setOpen] = useState(true);
  const prevCounts = useRef({});

  const poll = async () => {
    const [users, kyc, cod, tickets, trucks, wh] = await Promise.allSettled([
      appApi.get("admin/users"),
      appApi.get("admin/kyc/pending"),
      appApi.get("admin/cod/pending"),
      appApi.get("admin/tickets"),
      appApi.get("admin/truck-bookings"),
      appApi.get("admin/warehouses"),
    ]);
    const dayMs = 24 * 3600 * 1000;
    const next = {
      merchants: users.status === "fulfilled"
        ? (users.value.users || []).filter((u) => Date.now() - new Date(u.created_at).getTime() < dayMs).length : 0,
      kyc: kyc.status === "fulfilled" ? (kyc.value.queue || []).length : 0,
      cod: cod.status === "fulfilled" ? cod.value.total_merchants || 0 : 0,
      tickets: tickets.status === "fulfilled"
        ? (tickets.value.tickets || []).filter((t) => !["closed", "resolved"].includes(String(t.status).toLowerCase())).length : 0,
      trucks: trucks.status === "fulfilled"
        ? (trucks.value.bookings || []).filter((b) => b.status === "pending_allocation").length : 0,
      warehouses: wh.status === "fulfilled" ? wh.value.counts?.pending || 0 : 0,
    };
    setSnoozed((s) => {
      const copy = { ...s };
      for (const k of Object.keys(next)) {
        if ((next[k] || 0) > (prevCounts.current[k] || 0)) delete copy[k];
      }
      return copy;
    });
    prevCounts.current = next;
    setItems(next);
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = Object.entries(items).filter(
    ([k, n]) => n > 0 && (!snoozed[k] || Date.now() - snoozed[k] > SNOOZE_MS)
  );

  if (visible.length === 0) return null;

  if (!open)
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="admin-notifier-fab"
        aria-label="Open notifications"
        className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-obsidian text-sky-300 border-2 border-obsidian shadow-[4px_4px_0px_0px_#2563EB] flex items-center justify-center"
      >
        <Bell className="w-5 h-5" />
        <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-mono font-bold w-5 h-5 flex items-center justify-center">
          {visible.length}
        </span>
      </button>
    );

  return (
    <div data-testid="admin-notifier" className="fixed bottom-6 right-6 z-50 w-[340px] max-w-[calc(100vw-2rem)]">
      <div className="border-2 border-obsidian bg-white shadow-[8px_8px_0px_0px_#2563EB]">
        <div className="flex items-center justify-between px-4 py-3 bg-obsidian text-white">
          <p className="text-xs font-mono font-bold uppercase tracking-widest flex items-center gap-2">
            <Bell className="w-3.5 h-3.5 text-sky-300" /> Action needed · {visible.length}
          </p>
          <button onClick={() => setOpen(false)} aria-label="Minimize notifications" data-testid="admin-notifier-minimize" className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-zinc-100">
          <AnimatePresence>
            {visible.map(([key, count]) => {
              const meta = CARD_META[key];
              const Icon = meta.icon;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 40 }}
                  className="p-4"
                  data-testid={`notif-card-${key}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 bg-acid text-white flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold">{meta.label}</p>
                      <p className="text-xs font-mono text-zinc-500 mt-0.5" data-testid={`notif-count-${key}`}>{count} pending</p>
                      <Link
                        to={meta.href}
                        data-testid={`notif-review-${key}`}
                        className="inline-block mt-2 text-[11px] font-mono font-bold uppercase tracking-widest text-acid hover:underline"
                      >
                        {meta.cta} →
                      </Link>
                    </div>
                    <button
                      onClick={() => setSnoozed((s) => ({ ...s, [key]: Date.now() }))}
                      aria-label={`Snooze ${key}`}
                      data-testid={`notif-dismiss-${key}`}
                      className="text-zinc-400 hover:text-obsidian"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
        <p className="px-4 py-2.5 border-t border-zinc-100 text-[10px] font-mono text-zinc-400 uppercase tracking-widest">
          Snoozed cards return in 5 min if still pending
        </p>
      </div>
    </div>
  );
};
